<template>
  <Layout :show-side="showFilters" @close="showFilters = false">
    <div class="flex items-center justify-between">
      <h1 class="h1">{{ mine ? 'Mijn dossiers' : 'Dossiers' }}</h1>
      <div class="flex">
        <router-link :to="{ name: 'contacts.create' }" class="btn btn-blue px-3 md:px-4">
          <BaseIcon name="plus" size="20" class="md:hidden" />
          <span class="hidden md:inline">Dossier toevoegen</span>
        </router-link>
        <button class="btn btn-gray px-3 ml-1 hidden" @click="exportData">
          <BaseIcon name="download" />
        </button>
      </div>
    </div>
    <FilterSummary
      :filters="filters"
      :filterOptions="filterOptions"
      @toggle="showFilters = !showFilters"
      @clear="clearFilters"
    />
    <CompanySummary
      :stats="stats"
      :status="filters.status"
      @change-status="filters.status = $event"
      @change-pipeline="filters.pipeline = $event"
    />
    <div class="my-6" v-if="contacts.length">
      <div class="flex py-3 font-semibold">
        <ColumnSort class="w-2/3 sm:w-1/2 md:w-2/5 lg:w-4/12" column="name" @sort="setSort">Naam</ColumnSort>
        <ColumnSort
          class="hidden sm:flex sm:w-1/4 md:w-1/5 lg:w-2/12"
          column="manager"
          @sort="setSort"
        >Beheerder</ColumnSort>
        <ColumnSort class="w-1/3 sm:w-1/4 md:w-1/5 lg:w-2/12" column="status" @sort="setSort">Soort</ColumnSort>
        <ColumnSort
          class="hidden md:flex md:w-1/5 lg:w-2/12"
          column="pending"
          @sort="setSort"
        >Pre-Deals</ColumnSort>
        <ColumnSort class="hidden lg:flex" column="last_activity" @sort="setSort">Laatste actie</ColumnSort>
      </div>
      <div
        v-for="contact in contacts"
        :key="contact.id"
        class="flex py-3 border-t border-gray-200 hover:bg-gray-100"
      >
        <div class="w-2/3 sm:w-1/2 md:w-2/5 lg:w-4/12">
          <router-link
            :to="{ name: 'contacts.show', params: { id: contact.id } }"
            class="text-blue-600"
          >{{ contact.name }}</router-link>
        </div>
        <div
          class="hidden sm:block sm:w-1/4 md:w-1/5 lg:w-2/12"
        >{{ contact.manager ? contact.manager.name_short : '-' }}</div>
        <div class="w-1/3 sm:w-1/4 md:w-1/5 lg:w-2/12">
          <StatusBadge v-if="contact.status" :status="contact.status" />
          <template v-else>-</template>
        </div>
        <div class="hidden md:block md:w-1/5 lg:w-2/12">{{ contact.pending | formatMoney }}</div>
        <div
          class="hidden lg:block"
          :title="contact.last_activity"
        >{{ contact.last_activity | formatTimeAgo }}</div>
      </div>
    </div>
    <div v-else class="my-20">
      <div class="max-w-xl mx-auto flex items-center">
        <img src="/images/smallbee-icons-all_smallbee-icon-dossier.svg" class="w-24 h-24" />
        <div class="p-2">
          <h2 class="font-semibold">Geen dossiers gevonden</h2>
          <div
            class="text-gray-700"
          >Hier staan al je dossiers. Dit kunnen klanten zijn, potentiële klanten of ander soortige relaties.</div>
        </div>
      </div>
    </div>
    <Pagination :meta="meta" @change="setPage" />
    <template v-slot:side>
      <div class="flex justify-between items-center text-gray-400 mb-5">
        <div>Filters</div>
        <button @click="showFilters = false">
          <BaseIcon name="x" />
        </button>
      </div>
      <MineToggle class="mb-6" tabs />
      <FilterSelect :options="filterOptions.status" title="Soort" v-model="filters.status" />
      <FilterSelect
        v-if="!mine"
        :options="filterOptions.user"
        title="Beheerder"
        v-model="filters.user"
      />
      <FilterTags v-model="filters.tags" />
      <FilterCustomfield
        v-for="customfield in $store.state.settings.customfields"
        :customfield="customfield"
        :key="customfield.id"
        v-model="filters[customfield.identifier]"
      />
    </template>
  </Layout>
</template>

<script>
import store from "@/store.js";
import { FilteredListDataMixin } from "@/mixins";
import CompanySummary from "@/components/CompanySummary";
import FilterSummary from "@/components/FilterSummary";
import MineToggle from "@/components/MineToggle";
import FilterSelect from "@/components/FilterSelect";
import FilterTags from "@/components/FilterTags";
import FilterCustomfield from "@/components/FilterCustomfield";
import Pagination from "@/components/Pagination";
import ColumnSort from "@/components/ColumnSort";
import StatusBadge from "@/components/StatusBadge";

function createCustomfieldFilters(query) {
  let filters = {};
  store.state.settings.customfields.forEach(
    cf => (filters[cf.identifier] = query[cf.identifier])
  );
  return filters;
}

export default {
  mixins: [FilteredListDataMixin],

  components: {
    CompanySummary,
    FilterSummary,
    MineToggle,
    FilterSelect,
    FilterTags,
    FilterCustomfield,
    Pagination,
    ColumnSort,
    StatusBadge
  },

  props: {
    mine: Boolean
  },

  metaInfo() {
    return { title: this.mine ? "Mijn dossiers" : "Dossiers" };
  },

  data() {
    return {
      showFilters: false,
      filters: {
        pipeline: this.$route.query.pipeline,
        status: this.$route.query.status,
        user: this.$route.query.user,
        tags: this.$route.query.tags,
        ...createCustomfieldFilters(this.$route.query)
      },
      filterOptions: {
        status: [
          ...this.$store.getters.statusOptions,
          { value: "none", text: "Zonder soort" }
        ],
        user: [
          ...this.$store.getters.userOptions,
          { value: "none", text: "Zonder beheerder" }
        ]
      },
      contacts: [],
      stats: {}
    };
  },

  watch: {
    mine() {
      this.page = 1;
      this.filters.user = null;
      this.$store.commit("TOGGLE_MINE", this.mine);
    }
  },

  created() {
    this.$store.commit("TOGGLE_MINE", this.mine);
  },

  methods: {
    setData(data) {
      this.contacts = data.data;
      this.stats = data.stats;
    },

    exportData() {
      this.$store.commit("FLASH_ADD", {
        message: "Exporteren is nog niet mogelijk"
      });
    }
  }
};
</script>
